import { useEffect, useMemo, useState } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PaginationState } from '@tanstack/react-table';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useSearchParams } from 'react-router-dom';

import DocComparer from './components/DocComparer';
import InconsistencyRow from './components/InconsistencyRow';
import InconsistencyRunPopup from './components/InconsistencyRunPopup';
import { InconsistencySourceIcons, InconsistencyTypeIcons } from './constants';
import InconsistenciesEditorToolbar from './InconsistenciesEditorToolbar';
import {
  getInconsistencySourceDisplayName,
  getInconsistencySourceTooltip,
  getInconsistencyTypeDisplayName,
  getInconsistencyTypeTooltip,
  InconsistencySource,
  InconsistencyType,
} from '../../../../../../../backend/src/case/inconsistency/inconsistency-display';
import TooltipModal from '../../../../../components/molecules/Modals/TooltipModal';
import DocViewer from '../../components/DocViewer/FactDocViewer';
import IncludeCheckbox from '../components/IncludeCheckbox';
import { SubToolbar } from '../components/TableToolbar/SubToolbar';
import { TableWrapper } from '../components/TableWrapper';
import { PageName, TrackingEvents } from '@/analytics/events';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useGetCase } from '@/api/queries/useGetCase';
import { useGetInconsistencies } from '@/api/queries/useGetInconsistencies';
import EmptyState from '@/components/molecules/EmptyState';
import EmptyStateCreation from '@/components/molecules/EmptyStateCreation';
import Modal from '@/components/molecules/Modals/CustomModal';
import { useDocumentNavigation } from '@/hooks/useOpenDocViewer';
import { Inconsistency, InconsistencyFact, InconsistencyFilters } from '@/types';

const InconsistenciesEditor = () => {
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');
  const pageNumber = searchParams.get('page') ?? '0';

  const [searchQueryInput, setSearchQueryInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: parseInt(pageNumber), pageSize: 50 });
  const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);
  // Default hasRelevantFacts to 'true' to reduce noise
  const [filters, setFilters] = useState<InconsistencyFilters>({ hasRelevantFacts: true });

  const [isDocComparerOpen, setIsDocComparerOpen] = useState(false);
  const [docGroups, setDocGroups] = useState<{ [key: string]: InconsistencyFact[] } | null>(null);

  const [hasInconsistencyRun, setHasInconsistencyRun] = useState(false);
  const [isInconsistencyRunModalOpen, setIsInconsistencyRunModalOpen] = useState(false);

  const [showSourceModal, setShowSourceModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [selectedSource, setSelectedSource] = useState<InconsistencySource | null>(null);
  const [selectedType, setSelectedType] = useState<InconsistencyType | null>(null);

  const [areAllSelected, setAreAllSelected] = useState(false);

  const { closeDocViewer } = useDocumentNavigation();

  const { data, isLoading } = useGetInconsistencies({
    caseId,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filters,
    searchQuery,
  });
  const { data: responseCase } = useGetCase(caseId);

  const [selectedInconsistencyIds, setSelectedInconsistencyIds] = useLocalStorage<string[]>(
    `case-${caseId}-selected-inconsistency-ids`,
    [],
  );

  const updateInconsistencyToggle = (inconsistencyId: string) => {
    if (selectedInconsistencyIds.find((id) => id === inconsistencyId)) {
      setSelectedInconsistencyIds(selectedInconsistencyIds.filter((id) => id !== inconsistencyId));
    } else {
      setSelectedInconsistencyIds([...selectedInconsistencyIds, inconsistencyId]);
    }
  };

  const handleToggleAll = ({ checked }: { checked: boolean }) => {
    if (data) {
      if (checked) {
        setSelectedInconsistencyIds(data?.allInconsistencyIds);
      } else {
        setSelectedInconsistencyIds([]);
      }
    }
  };

  useEffect(() => {
    checkIfAllSelected();
  }, [selectedInconsistencyIds.length, data]);

  function checkIfAllSelected() {
    if (!caseId) throw new Error('No caseId found');

    if (!selectedInconsistencyIds.length) return setAreAllSelected(false);

    const allSelected = data?.allInconsistencyIds.every((i) => selectedInconsistencyIds.includes(i));

    setAreAllSelected(allSelected ?? false);
  }

  const handleSourceClick = (source: InconsistencySource) => {
    setSelectedSource(source);
    setShowSourceModal(true);
  };
  const handleTypeClick = (type: InconsistencyType) => {
    setSelectedType(type);
    setShowTypeModal(true);
  };

  // Pagination
  const goToPage = (val: number) => {
    const newPaginationState = { pageIndex: val, pageSize: pagination.pageSize };
    setPagination(newPaginationState);
  };
  const prevPage = () => goToPage(pagination.pageIndex - 1);
  const nextPage = () => goToPage(pagination.pageIndex + 1);

  const docId = searchParams.get('docId');
  const factId = searchParams.get('factId');

  const onSearchCall = async () => {
    trackCustomEvent(TrackingEvents.SearchInconsistencies);
    // Reset page to 0
    const newPaginationState = { pageIndex: 0, pageSize: pagination.pageSize };
    setPagination(newPaginationState);
    closeDocViewer();
    setSearchQuery(searchQueryInput); // Set the actual filter used for fetching
  };

  const clearSearch = async () => {
    setSearchQueryInput('');
    setSearchQuery('');
    await new Promise((resolve) => setTimeout(resolve, 0));
  };

  const openDocComparer = (docGroups: { [key: string]: InconsistencyFact[] }) => {
    setDocGroups(docGroups);
    setIsDocComparerOpen(true);
    trackCustomEvent(TrackingEvents.OpenInconsistencyDocComparer);
  };

  const closeDocComparer = () => {
    setIsDocComparerOpen(false);
    setDocGroups(null);
  };

  useEffect(() => {
    if (docId) {
      setIsDocViewerOpen(true);
    } else {
      setIsDocViewerOpen(false);
    }
  }, [docId]);

  useEffect(() => {
    if (responseCase) {
      setHasInconsistencyRun(responseCase.hasInconsistencyRun ?? false);
    }
  }, [responseCase]);

  useEffect(() => {
    trackPageView(PageName.Inconsistencies);
  }, []);

  return (
    <div className="flex h-full w-full">
      <div className="flex h-full w-full flex-col transition-all duration-300 ease-in-out">
        {caseId && (
          <InconsistenciesEditorToolbar
            caseId={caseId}
            selectedInconsistencyIds={selectedInconsistencyIds}
            totalCount={data?.totalCount ?? 0}
            globalFilter={searchQueryInput}
            setGlobalFilter={setSearchQueryInput}
            onSearchCall={onSearchCall}
            clearSearch={clearSearch}
            totalPages={data?.pageCount}
            goToPage={goToPage}
            currentPage={pagination.pageIndex}
            prevPage={prevPage}
            nextPage={nextPage}
            activeFilters={filters}
            setFilters={setFilters}
          />
        )}

        <SubToolbar
          entityType="inconsistencies"
          totalCount={data?.totalCount}
          totalUnfilteredCount={data?.totalUnfilteredCount}
          selectedCount={selectedInconsistencyIds.length}
          handleToggleAll={async ({ checked }) => handleToggleAll({ checked })}
          deselectAll={() => handleToggleAll({ checked: false })}
          allRowsChecked={areAllSelected}
        />

        <TableWrapper className="flex">
          {hasInconsistencyRun ? (
            <>
              {isLoading ? (
                <div className="flex h-full min-h-[500px] w-full items-center justify-center">
                  <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
                </div>
              ) : (
                <>
                  {caseId && data && data.inconsistencies && data.inconsistencies.length > 0 ? (
                    <div className="flex h-full w-full">
                      <div
                        className={`h-full overflow-auto transition-all duration-300 ${isDocViewerOpen ? 'w-7/12' : 'w-full'}`}
                      >
                        <div className="flex w-full flex-col gap-3 py-1">
                          {data.inconsistencies.map((inconsistency: Inconsistency) => {
                            return (
                              <div key={inconsistency.id} className="relative flex gap-3">
                                <IncludeCheckbox
                                  included={selectedInconsistencyIds.includes(inconsistency.id)}
                                  onClick={() => updateInconsistencyToggle(inconsistency.id)}
                                  eventId={inconsistency.id}
                                />
                                <div className="flex w-full rounded-lg border pl-2">
                                  <InconsistencyRow
                                    inconsistency={inconsistency}
                                    caseId={caseId}
                                    openDocComparer={openDocComparer}
                                    onSourceClick={handleSourceClick}
                                    onTypeClick={handleTypeClick}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div
                        className={`transition-all duration-300 ease-in-out ${
                          isDocViewerOpen ? 'ml-2 w-5/12 opacity-100' : 'w-0 opacity-0'
                        } overflow-hidden rounded border-2 bg-white`}
                      >
                        <div className={`h-full w-full transition-transform duration-300 ease-in-out`}>
                          {docId && factId && caseId && <DocViewer docId={docId} caseId={caseId} factId={factId} />}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <EmptyState
                      title="No inconsistencies found"
                      subtitle={
                        (filters && Object.keys(filters).length > 0) || pagination.pageIndex > 0
                          ? "Sorry, we couldn't find any inconsistencies matching these criteria."
                          : "Sorry, we couldn't find any inconsistencies for this case."
                      }
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <EmptyStateCreation
              title="Inconsistencies not yet generated"
              subtitle="Click the button below if you want to generate inconsistencies for this case."
              onClick={() => {
                setIsInconsistencyRunModalOpen(true);
              }}
            />
          )}
        </TableWrapper>
      </div>
      {caseId && docGroups && (
        <Modal
          isOpen={isDocComparerOpen}
          handleClose={closeDocComparer}
          content={<DocComparer caseId={caseId} docGroups={docGroups} />}
          maxWidth="84rem"
        />
      )}
      {selectedSource && (
        <TooltipModal
          title={getInconsistencySourceDisplayName(selectedSource)}
          header={(() => {
            const { Icon, className } = InconsistencySourceIcons[selectedSource];
            return (
              <div className={`flex h-full w-full items-center justify-center ${className}`}>
                <Icon />
              </div>
            );
          })()}
          content={getInconsistencySourceTooltip(selectedSource)}
          subtext="Inconsistencies can be, same claim, multiple claims, claim vs evidence, evidence vs evidence."
          isOpen={showSourceModal}
          handleClose={() => setShowSourceModal(false)}
        />
      )}
      {selectedType && (
        <TooltipModal
          title={getInconsistencyTypeDisplayName(selectedType)}
          header={(() => {
            const { Icon, className } = InconsistencyTypeIcons[selectedType];
            return (
              <div className={`flex h-full w-full items-center justify-center ${className}`}>
                <Icon />
              </div>
            );
          })()}
          content={getInconsistencyTypeTooltip(selectedType)}
          subtext="Inconsistency types can be, dates, events, changing testimony, details, observations or fabrications. There can be overlaps in these types."
          isOpen={showTypeModal}
          handleClose={() => setShowTypeModal(false)}
        />
      )}
      <Modal
        isOpen={isInconsistencyRunModalOpen}
        handleClose={() => {
          setIsInconsistencyRunModalOpen(false);
        }}
        content={
          <InconsistencyRunPopup caseId={caseId ?? ''} closeModal={() => setIsInconsistencyRunModalOpen(false)} />
        }
      />
    </div>
  );
};

export default InconsistenciesEditor;
